/**
 * The SPA does not depend on `@steward-app/cash-flow` (it is the backend's classifier), so the keys
 * are written out here and `categoryNames.test.ts` pins them against the package's
 * `CASH_FLOW_CATEGORY_KEYS`, both directions.
 *
 * The one place display names for the machine category keys live (`@steward-app/cash-flow`
 * `codes.ts`: "the SPA owns presentation"). Matt's list of 2026-11-02 (steward-app#2771). Keyed by
 * the key a transaction row's `category` carries: a Plaid primary that did not split, or a
 * custom key (`rent`, `utilities`, `card-payment`, `savings`, `investing`, ...). An unknown key (Plaid can add a primary) is rendered as sentence case
 * by {@link categoryName} rather than shown raw.
 */
export const UNCATEGORIZED_CATEGORY = 'UNCATEGORIZED';

const NAMES: Record<string, string> = {
  INCOME: 'Income',
  LOAN_DISBURSEMENTS: 'Loan proceeds',
  LOAN_PAYMENTS: 'Loan payments',
  TRANSFER_IN: 'Transfers in',
  TRANSFER_OUT: 'Transfers out',
  BANK_FEES: 'Bank fees',
  ENTERTAINMENT: 'Entertainment',
  FOOD_AND_DRINK: 'Food and drink',
  GENERAL_MERCHANDISE: 'Shopping',
  HOME_IMPROVEMENT: 'Home improvement',
  MEDICAL: 'Medical',
  PERSONAL_CARE: 'Personal care',
  insurance: 'Insurance',
  services: 'Services',
  'taxes-and-government': 'Taxes and government',
  giving: 'Giving',
  TRANSPORTATION: 'Transportation',
  TRAVEL: 'Travel',
  rent: 'Rent',
  utilities: 'Utilities',
  OTHER: 'Other',
  'card-payment': 'Card payments',
  savings: 'Savings',
  investing: 'Investing',
  [UNCATEGORIZED_CATEGORY]: 'Uncategorized',
};

/**
 * Keys an older stored month total or a family correction may still carry (before steward-app#2771
 * split these), read as the key that now holds them. Mirrors the package's `normalizeCategoryKey`;
 * `categoryNames.test.ts` pins the two together. Once the nightly recompute has rewritten a month,
 * only the new keys remain in its totals.
 */
export const LEGACY: Record<string, string> = {
  RENT_AND_UTILITIES: 'utilities',
  GOVERNMENT_AND_NON_PROFIT: 'taxes-and-government',
  GENERAL_SERVICES: 'services',
  'savings-and-investing': 'investing',
};
export function canonicalCategoryKey(key: string): string {
  return Object.prototype.hasOwnProperty.call(LEGACY, key) ? LEGACY[key]! : key;
}

/** Filter key and label for a row whose effective flow is internal: it is never shown under its Plaid category. */
export const INTERNAL_FILTER_KEY = '__internal__';
export const INTERNAL_CATEGORY_LABEL = 'Moved between your accounts';

/** The category key a row is shown and filtered under. */
export function rowCategoryKey(r: { flow?: string; category?: string; userCategory?: string; pfcPrimary?: string }): string {
  if (r.flow === 'internal') return INTERNAL_FILTER_KEY;
  const k = r.category ?? r.userCategory ?? r.pfcPrimary ?? UNCATEGORIZED_CATEGORY;
  return canonicalCategoryKey(k);
}

export function categoryName(key: string | undefined): string {
  if (key === INTERNAL_FILTER_KEY) return INTERNAL_CATEGORY_LABEL;
  if (!key) return NAMES[UNCATEGORIZED_CATEGORY]!;
  key = canonicalCategoryKey(key);
  if (Object.prototype.hasOwnProperty.call(NAMES, key)) return NAMES[key]!;
  const words = key.toLowerCase().replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The keys a family may choose as a correction, in display order of their names. */
export const CHOOSABLE_CATEGORIES: readonly { key: string; name: string }[] = Object.keys(NAMES).filter(k => k !== UNCATEGORIZED_CATEGORY)
  .map(key => ({ key, name: categoryName(key) }))
  .sort((a, b) => a.name.localeCompare(b.name));

/** Every key a row's `category` can read as, for the Activity filter: the choosable ones plus Uncategorized. */
export const FILTER_CATEGORIES: readonly { key: string; name: string }[] = [
  ...CHOOSABLE_CATEGORIES,
  { key: UNCATEGORIZED_CATEGORY, name: categoryName(UNCATEGORIZED_CATEGORY) },
  { key: INTERNAL_FILTER_KEY, name: INTERNAL_CATEGORY_LABEL },
].sort((a, b) => a.name.localeCompare(b.name));

/**
 * The name for a key the board groups by: a key this file does not know (Plaid can add a primary)
 * reads as Other, so the board never shows a stray second row. The Activity list uses
 * {@link categoryName} instead, which spells an unknown key out.
 */
export function boardCategoryName(key: string): string {
  if (key === '') return NAMES[UNCATEGORIZED_CATEGORY]!;
  key = canonicalCategoryKey(key);
  return Object.prototype.hasOwnProperty.call(NAMES, key) ? NAMES[key]! : NAMES['OTHER']!;
}
